import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  ArrowLeftIcon,
  ChatBubble,
  ClassityLogo,
  DashboardIcon,
  FloatingControl,
  GroupIcon,
  MoreIcon,
  Participants,
  Sheet,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Stack, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import React, { Suspense, useState } from 'react';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';

const TextEditor = React.lazy(() =>
  import('@classity-edtech/ui-base/dist/components/Displays').then(mod => ({
    default: mod.TextEditor,
  })),
);

export interface HomeProps extends MuiBoxProps {}

export const ChatRoom = ({ className }: HomeProps) => {
  const screens = useAppMediaQuery();
  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [showMessages, setShowMessages] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const [content, setContent] = useState('');

  const toggleShowMessages = () => {
    setShowMessages(value => !value);
  };

  const toggleOpenSheet = () => {
    setOpenSheet(value => !value);
  };

  const participants = {
    groupChat: {
      avatarSrc:
        'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
      title: 'Group - Genius Grove',
      description: 'I can get you to a level of good understanding, no problem!',
      time: '12:25',
      notification: '2',
      isActive: true,
      onClick: toggleShowMessages,
    },
    tutorChat: {
      avatarSrc:
        'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
      title: 'Kemi Balogun',
      description: 'Could you update me on your financial status?',
      time: 'Yesterday',
      isActive: false,
      showActiveIndicator: true,
      onClick: toggleShowMessages,
    },
    peersChat: [
      {
        avatarSrc:
          'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
        title: 'Femi Adewale',
        description: 'Prove that (√3 + 1) (3 – cot 30°) = tan360° – 2 sin 60°',
        time: '2 days ago',
        isActive: false,
        onClick: toggleShowMessages,
      },
      {
        avatarSrc:
          'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
        title: 'Ebegue-Uki Osagie',
        description: 'In the right triangle PQR, Q is right angle.',
        time: '3 days ago',
        isActive: false,
        onClick: toggleShowMessages,
      },
    ],
  };

  const floatingControls = {
    video: {
      action: () => {},
      isActive: false,
    },
    audio: {
      action: () => {},
      isActive: true,
    },
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {(!isSmallScreen || !showMessages) && (
        <Participants
          sx={{
            borderRight: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            height: { xs: 'calc(100dvh - 98px)', md: 'calc(100dvh - 66px)' },
            width: { xs: '100%', md: 'max-content' },
            minWidth: { xs: '100%', md: '345px' },
            borderRadius: '8px 0 0 8px',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
          {...participants}
        />
      )}
      {(!isSmallScreen || showMessages) && (
        <Stack sx={{ justifyContent: 'space-between', width: '100%', position: 'relative' }}>
          <Box
            sx={{
              width: '100%',
              height: '66px',
              borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
            }}
          >
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <GroupIcon style={{ width: '48px', height: '48px' }} />
                <Stack sx={{ gap: 0.5, justifyContent: 'space-between' }}>
                  <Typography variant='body2SemiBold'>Genius Grove</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DashboardIcon sx={{ width: '14px', height: '14px' }} />
                    <Typography variant='body3'>Follow</Typography>
                  </Box>
                </Stack>
              </Box>
              <Stack sx={{ gap: '1.5', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <MoreIcon
                  sx={{
                    transform: 'rotate(90deg)',
                    color: globalColours['$global-colour-interactive-secondary-default'],
                  }}
                />
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: globalColours['$global-colour-interactive-secondary-active'],
                      px: { xs: 1, md: 2 },
                      cursor: 'pointer',
                    }}
                    onClick={isSmallScreen ? toggleOpenSheet : () => {}}
                  >
                    {isSmallScreen && (
                      <ArrowLeftIcon
                        sx={{ width: '14px', height: '14px', display: { xs: 'block', md: 'none' } }}
                      />
                    )}
                    Chat
                  </Typography>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: globalColours['$global-colour-interactive-secondary-default'],
                      px: { xs: 1, md: 2 },
                      cursor: 'pointer',
                    }}
                  >
                    <ArrowLeftIcon
                      sx={{ width: '14px', height: '14px', transform: 'rotate(-90deg)' }}
                    />
                    Worksheet #1
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Stack
            sx={{
              height: { xs: 'calc(100dvh - 226px)', md: 'calc(100dvh - 272px)' },
              overflow: 'auto',
              px: { xs: 2.5, md: 1 },
              py: 3,
              gap: 1.5,
            }}
          >
            <ChatBubble
              avatarSrc='https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4'
              chatType='sender'
              text='Lorem ipsum dolor sit amet, consectetur adipiscing elit. Quisque leo orci, egestas in imperdiet rutrum'
              deleteAction={() => {}}
              editAction={() => {}}
            />
            <ChatBubble
              avatarSrc='https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4'
              chatType='recipient'
              text='Aenean vel ligula nec ante hendrerit laoreet in vestibulum elit'
              deleteAction={() => {}}
              editAction={() => {}}
            />
            <ChatBubble
              avatarSrc='https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4'
              chatType='recipient'
              text='Cras ultrices pretium sem, sit amet tempor urna bibendum a. Sed dictum at ligula id ullamcorper'
              deleteAction={() => {}}
              editAction={() => {}}
            />

            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                p: 1.5,
                borderRadius: '16px',
                border: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                width: 'fit-content',
              }}
            >
              <Box
                sx={{
                  backgroundColor: globalColours['$global-colour-content-tertiary'],
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                }}
              ></Box>
              <Box
                sx={{
                  backgroundColor: globalColours['$global-colour-content-tertiary'],
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                }}
              ></Box>
              <Box
                sx={{
                  backgroundColor: globalColours['$global-colour-content-tertiary'],
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                }}
              ></Box>
            </Box>
          </Stack>

          <Box
            sx={{
              height: { sm: '46px', md: '140px' },
              px: { xs: 2.5, md: 0 },
              py: { xs: 1, md: 0 },
            }}
          >
            {typeof window !== 'undefined' && (
              <Suspense fallback={<div>Loading editor...</div>}>
                <TextEditor content={content} setContent={setContent} sendMessage={() => {}} />
              </Suspense>
            )}
          </Box>

          <Sheet
            closeSheet={toggleOpenSheet}
            openSheet={openSheet}
            body={
              <Participants sx={{ width: { xs: '100%', md: 'max-content' } }} {...participants} />
            }
          />
        </Stack>
      )}
      <FloatingControl {...floatingControls} />
    </Box>
  );
};

ChatRoom.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ChatRoom.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ChatRoom)(
  ({ theme }) => `
`,
);
